import { UnitSystem } from '@/interfaces/units';
import { IConcreteDesignImperial, ConcreteDesignMetric } from '@/modules/design/interfaces/newConcrete';
import {
  CM_TO_INCHES,
  DEGC_TO_DEGF,
  DEGF_TO_DEGC,
  INCHES_TO_CM,
  KPH_TO_MPH,
  MPA_TO_PSI,
  MPH_TO_KPH,
  PSI_TO_MPA,
  PSIPERIN_TO_MPAPERCM,
  MPAPERCM_TO_PSIPERIN,
  roundAfterUnitConversion,
} from '@/lib/unitConversion/unitConversionFactors';
import { DesignScenarioType } from '@/modules/design/interfaces/shared';
import { BaseScenarioType } from '@/lib/types/IBaseScenario';

const truncate = function (v: number): number {
  return Number((v).toFixed(2));
};

export const concreteImperialToMetric = function (A: IConcreteDesignImperial): ConcreteDesignMetric {
  const B: ConcreteDesignMetric = {
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: UnitSystem.Metric,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.concrete,
    unitSystem: UnitSystem.Metric,
    isComputedFromLegacy: A.isComputedFromLegacy,

    // IDesignBaseScenario information
    name: A.name,
    description: A.description,
    state: A.state,

    // Pavement Design
    completionYear: A.completionYear, // Years
    roadwayClassification: A.roadwayClassification,
    pavementType: A.pavementType,


    // Design Parameters
    designPeriod_years: A.designPeriod_years, // Years
    reliabilityLevel: A.reliabilityLevel, // Unitless, Standard Normal Deviate
    standardError: A.standardError, // Unitless,

    initialServiceability: A.initialServiceability, // Present Servicability Index (p_i)
    terminalServiceability: A.terminalServiceability, // Present Servicability Index at EOL(p_t)
    deltaServiceability: A.deltaServiceability, // TODO: Redundant -> calculated???
    nearestCity: A.nearestCity,

    meanWindSpeed_kph: A.meanWindSpeed_mph ? roundAfterUnitConversion(A.meanWindSpeed_mph * MPH_TO_KPH) : A.meanWindSpeed_mph,
    meanTemp_degC: A.meanTemp_degF ? roundAfterUnitConversion(DEGF_TO_DEGC(A.meanTemp_degF)) : A.meanTemp_degF,
    meanPrecip_cm: A.meanPrecip_in ? roundAfterUnitConversion(A.meanPrecip_in * INCHES_TO_CM) : A.meanPrecip_in,

    // Traffic and Loading
    esalCalculatedFrom: A.esalCalculatedFrom,

    // From AADT
    initialTraffic: A.initialTraffic, // Completion year traffic
    trafficGrowthRate: A.trafficGrowthRate,
    esalGrowthRate: A.esalGrowthRate,

    // From ESALs
    esals: A.esals,
    loadEquivalencyFactor: A.loadEquivalencyFactor,

    // From Design ESALs
    totalDesignEsals: A.totalDesignEsals,

    // Pavement Structure
    ruptureModulus_mpa: A.ruptureModulus_psi ? roundAfterUnitConversion(A.ruptureModulus_psi * PSI_TO_MPA) : A.ruptureModulus_psi,
    elasticModulus_mpa: A.elasticModulus_psi ? roundAfterUnitConversion(A.elasticModulus_psi * PSI_TO_MPA) : A.elasticModulus_psi,
    poissonsRatio: A.poissonsRatio,

    jointSpacing_cm: A.jointSpacing_in ? roundAfterUnitConversion(A.jointSpacing_in * INCHES_TO_CM) : A.jointSpacing_in,
    loadTransferCoefficient: A.loadTransferCoefficient,
    edgeSupport: A.edgeSupport,


    // Pavement Substructure
    baseLayerType: A.baseLayerType,
    baseModulus_mpa: A.baseModulus_psi ? roundAfterUnitConversion(A.baseModulus_psi * PSI_TO_MPA) : A.baseModulus_psi,
    baseThickness_cm: A.baseThickness_in ? roundAfterUnitConversion(A.baseThickness_in * INCHES_TO_CM) : A.baseThickness_in,
    drainageFactor: A.drainageFactor,
    slabFrictionCoefficient: A.slabFrictionCoefficient,
    subgradeReactionModulus_mpaPerM: A.subgradeReactionModulus_psiPerIn ? roundAfterUnitConversion(A.subgradeReactionModulus_psiPerIn * PSIPERIN_TO_MPAPERCM) : A.subgradeReactionModulus_psiPerIn,

  };

  return B;
};


export const concreteMetricToImperial = function (A: ConcreteDesignMetric): IConcreteDesignImperial {
  const B: IConcreteDesignImperial = {
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.concrete,
    unitSystem: UnitSystem.Imperial,
    isComputedFromLegacy: A.isComputedFromLegacy,

    // IDesignBaseScenario information
    name: A.name,
    description: A.description,
    state: A.state,

    // Pavement Design
    completionYear: A.completionYear, // Years
    roadwayClassification: A.roadwayClassification,
    pavementType: A.pavementType,


    // Design Parameters
    designPeriod_years: A.designPeriod_years, // Years
    reliabilityLevel: A.reliabilityLevel, // Unitless, Standard Normal Deviate
    standardError: A.standardError, // Unitless,

    initialServiceability: A.initialServiceability, // Present Servicability Index (p_i)
    terminalServiceability: A.terminalServiceability, // Present Servicability Index at EOL(p_t)
    deltaServiceability: A.deltaServiceability, // TODO: Redundant -> calculated???
    nearestCity: A.nearestCity,

    meanWindSpeed_mph: A.meanWindSpeed_kph ? roundAfterUnitConversion(A.meanWindSpeed_kph * KPH_TO_MPH) : A.meanWindSpeed_kph,
    meanTemp_degF: A.meanTemp_degC ? roundAfterUnitConversion(DEGC_TO_DEGF(A.meanTemp_degC)) : A.meanTemp_degC,
    meanPrecip_in: A.meanPrecip_cm ? roundAfterUnitConversion(A.meanPrecip_cm * CM_TO_INCHES) : A.meanPrecip_cm,

    // Traffic and Loading
    esalCalculatedFrom: A.esalCalculatedFrom,

    // From AADT
    initialTraffic: A.initialTraffic, // Completion year traffic
    trafficGrowthRate: A.trafficGrowthRate,
    esalGrowthRate: A.esalGrowthRate,

    // From ESALs
    esals: A.esals,
    loadEquivalencyFactor: A.loadEquivalencyFactor,

    // From Design ESALs
    totalDesignEsals: A.totalDesignEsals,

    // Pavement Structure
    ruptureModulus_psi: A.ruptureModulus_mpa ? roundAfterUnitConversion(A.ruptureModulus_mpa * MPA_TO_PSI) : A.ruptureModulus_mpa,
    elasticModulus_psi: A.elasticModulus_mpa ? roundAfterUnitConversion(A.elasticModulus_mpa * MPA_TO_PSI) : A.elasticModulus_mpa,
    poissonsRatio: A.poissonsRatio,

    jointSpacing_in: A.jointSpacing_cm ? roundAfterUnitConversion(A.jointSpacing_cm * CM_TO_INCHES) : A.jointSpacing_cm,
    loadTransferCoefficient: A.loadTransferCoefficient,
    edgeSupport: A.edgeSupport,


    // Pavement Substructure
    baseLayerType: A.baseLayerType,
    baseModulus_psi: A.baseModulus_mpa ? roundAfterUnitConversion(A.baseModulus_mpa * MPA_TO_PSI) : A.baseModulus_mpa,
    baseThickness_in: A.baseThickness_cm ? roundAfterUnitConversion(A.baseThickness_cm * CM_TO_INCHES) : A.baseThickness_cm,
    drainageFactor: A.drainageFactor,
    slabFrictionCoefficient: A.slabFrictionCoefficient,
    subgradeReactionModulus_psiPerIn: A.subgradeReactionModulus_mpaPerM ? roundAfterUnitConversion(A.subgradeReactionModulus_mpaPerM * MPAPERCM_TO_PSIPERIN) : A.subgradeReactionModulus_mpaPerM,

  };

  return B;
};
